import React, { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { useLocation, useNavigate } from "react-router-dom";
import { Y, T, CREDIT } from "../theme";
import { api } from "../firebase";
import { useNotify } from "../App";
import { CreditAmount } from "./shared";

// Stripe publishable key (live mode)
const stripePromise = loadStripe("pk_live_51TFLOCRuIIYUUWfWQZYWA9FN61m6me0OLjWTwCRcsZoXHpAgBg2O4iTGhgtCsEAKisZ8h3J52WwAfTC6iUm7r6Pv004WIlTgJT");

// Stripe appearance — dark theme matching the app
const stripeAppearance = {
  theme: "night",
  variables: {
    colorPrimary: CREDIT,
    colorBackground: T.dark,
    colorText: T.text,
    colorDanger: "#FF2D6F",
    fontFamily: "Mulish, sans-serif",
    borderRadius: "4px",
    fontSizeBase: "12px",
    fontSizeSm: "10px",
    fontWeightNormal: "400",
    spacingUnit: "3px",
    spacingGridRow: "12px",
  },
  rules: {
    ".Label": {
      fontSize: "10px",
      fontWeight: "600",
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      color: T.text2,
      marginBottom: "4px",
    },
    ".Input": {
      backgroundColor: "rgba(255,255,255,0.03)",
      border: `1px solid rgba(255,255,255,0.1)`,
      borderRadius: "4px",
      padding: "8px 10px",
      fontSize: "12px",
      color: T.text,
      transition: "border-color 0.15s",
    },
    ".Input:focus": {
      borderColor: CREDIT,
      boxShadow: "none",
    },
    ".Tab": {
      backgroundColor: "rgba(255,255,255,0.03)",
      border: "1px solid rgba(255,255,255,0.1)",
      borderRadius: "4px",
      fontSize: "11px",
      padding: "6px 10px",
    },
    ".Tab--selected": {
      backgroundColor: `${CREDIT}15`,
      borderColor: CREDIT,
      color: CREDIT,
    },
    ".Tab:hover": {
      borderColor: "rgba(255,255,255,0.2)",
    },
    ".Error": {
      fontSize: "10px",
    },
  },
};

// ── CheckoutForm (inner Stripe component) ────────────────────────────────────
function CheckoutForm({ pkg, onCancel, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [status, setStatus] = useState("ready"); // ready | processing | success | error
  const [error, setError] = useState(null);
  const [elementReady, setElementReady] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setStatus("processing");
    setError(null);

    const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: "if_required",
    });

    if (stripeError) {
      setError(stripeError.message);
      setStatus("error");
    } else {
      // Credit the user immediately via confirm-payment endpoint
      try {
        if (paymentIntent?.id) await api.confirmPayment(paymentIntent.id);
      } catch (e) {
        console.warn("confirm-payment call failed (webhook will retry):", e);
      }
      setStatus("success");
      onSuccess();
    }
  };

  if (status === "success") {
    return (
      <div style={{ textAlign: "center", padding: "40px 0" }}>
        <div style={{
          width: 64, height: 64, borderRadius: "50%", background: `${CREDIT}20`,
          display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px",
        }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={CREDIT} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        </div>
        <div style={{ color: CREDIT, fontSize: 20, fontWeight: 800, marginBottom: 8 }}>Payment Successful!</div>
        <div style={{ color: T.text2, fontSize: 14, marginBottom: 28 }}>
          <CreditAmount amount={pkg.credits} size={16} /> has been added to your balance
        </div>
        <button onClick={onCancel} className="s-toggle" style={{
          padding: "12px 40px", borderRadius: 10, background: CREDIT, border: "none",
          color: T.dark, fontWeight: 700, fontSize: 14, cursor: "pointer",
        }}>Done</button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      {!elementReady && !loadError && (
        <div style={{ textAlign: "center", padding: "30px 0", color: T.text3, fontSize: 13 }}>Loading payment methods\u2026</div>
      )}
      {loadError && (
        <div style={{ color: T.danger, fontSize: 12, padding: "16px 0", textAlign: "center" }}>
          Payment element failed to load: {loadError}
        </div>
      )}
      <div style={{ minHeight: elementReady ? "auto" : 0, overflow: elementReady ? "visible" : "hidden" }}>
        <PaymentElement
          options={{ layout: "accordion" }}
          onReady={() => setElementReady(true)}
          onLoadError={(e) => setLoadError(e.error?.message || "Unknown error")}
        />
      </div>

      {error && (
        <div style={{ color: T.danger, fontSize: 12, marginTop: 12, padding: "8px 12px", borderRadius: 8, background: T.dangerBg }}>
          {error}
        </div>
      )}

      {elementReady && (
        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <button type="button" onClick={onCancel} className="s-toggle" style={{
            flex: 1, padding: "12px 0", borderRadius: 8,
            border: `1px solid ${T.text3}`, background: "transparent",
            color: T.text2, fontWeight: 600, fontSize: 13, cursor: "pointer",
          }}>Cancel</button>
          <button
            type="submit"
            disabled={!stripe || status === "processing"}
            className="s-toggle"
            style={{
              flex: 2, padding: "12px 0", borderRadius: 8,
              background: CREDIT, border: "none", color: T.dark, fontWeight: 700, fontSize: 13,
              cursor: status === "processing" ? "not-allowed" : "pointer",
              opacity: status === "processing" ? 0.7 : 1,
              letterSpacing: "0.02em",
            }}>
            {status === "processing" ? "Processing\u2026" : `Pay $${pkg.price.toFixed(2)}`}
          </button>
        </div>
      )}
    </form>
  );
}

// ── PurchaseView (full page) ─────────────────────────────────────────────────
export default function PurchaseView() {
  const location = useLocation();
  const navigate = useNavigate();
  const { notify } = useNotify();
  const pkg = location.state; // { credits, price, fee }

  const [clientSecret, setClientSecret] = useState(null);
  const [loadError, setLoadError] = useState(null);

  // Redirect if no package data
  useEffect(() => {
    if (!pkg) { navigate("/bank", { replace: true }); return; }
    api.createPaymentIntent(String(pkg.credits))
      .then(({ clientSecret }) => setClientSecret(clientSecret))
      .catch(err => setLoadError(err.message));
  }, []);

  if (!pkg) return null;

  const handleCancel = () => navigate("/bank");

  const handleSuccess = () => {
    api.getSettings().then(() => {}).catch(() => {});
    notify("success", "Credits purchased successfully!", 3000);
    // Don't navigate immediately — let success screen show, user clicks Done
  };

  return (
    <div style={{ maxWidth: 480, margin: "0 auto", padding: "0 16px" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 28 }}>
        <button onClick={handleCancel} className="s-toggle" style={{
          width: 32, height: 32, borderRadius: 8, border: "none",
          background: "rgba(255,255,255,0.06)", color: T.text2,
          cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
        <div style={{ color: CREDIT, fontSize: 18, fontWeight: 800 }}>Purchase Credits</div>
      </div>

      {/* Package summary */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "16px 20px", borderRadius: 12, background: "rgba(255,255,255,0.03)",
        border: `1px solid ${CREDIT}30`, marginBottom: 24,
      }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 4, color: CREDIT, fontSize: 16, fontWeight: 700 }}><CreditAmount amount={pkg.credits} size={16} /> Credits</div>
        </div>
        <div style={{ color: Y, fontSize: 28, fontWeight: 900 }}>${pkg.price.toFixed(2)}</div>
      </div>

      {/* Stripe checkout */}
      {loadError ? (
        <div style={{ color: T.danger, fontSize: 13, padding: "24px 0", textAlign: "center" }}>
          {loadError}
          <div style={{ marginTop: 16 }}>
            <button onClick={handleCancel} className="s-toggle" style={{
              padding: "10px 28px", borderRadius: 8, border: `1px solid ${T.text3}`,
              background: "transparent", color: T.text2, fontSize: 13, cursor: "pointer",
            }}>Go Back</button>
          </div>
        </div>
      ) : !clientSecret ? (
        <div style={{ textAlign: "center", padding: "48px 0", color: T.text3, fontSize: 13 }}>
          Setting up payment\u2026
        </div>
      ) : (
        <Elements stripe={stripePromise} options={{ clientSecret, appearance: stripeAppearance }}>
          <CheckoutForm pkg={pkg} onCancel={handleCancel} onSuccess={handleSuccess} />
        </Elements>
      )}
    </div>
  );
}
